import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { APP_CATEGORIES, appBenefits, appScreenshots, categoryLabel, client, type AppCategory, type Application, type AppInput, type Department, type Feature } from '../lib/api';
import { AppStatusChip } from './StatusChip';
import { AppMonogram } from './Page';
import { tokens } from '../theme';

const STATUSES: Application['status'][] = ['live', 'beta', 'planned', 'maintenance'];
const NEW_DEPT = '__new__';

type Draft = AppInput & { id: string | null };

function emptyDraft(departmentId = ''): Draft {
  return {
    id: null,
    name: '',
    slug: '',
    department_id: departmentId,
    tagline: '',
    description: '',
    audience: '',
    status: 'live',
    url: '',
    stack: '',
    version: '',
    sort_order: 100,
    logo_url: '',
    category: 'custom',
    screenshots: [],
    developer: '',
    time_saved: '',
    benefits: [''],
    show_on_status: true,
    cost_saved: '',
    impact_note: '',
    features: [{ title: '', detail: '' }],
  };
}

function draftFrom(app: Application, features: Feature[]): Draft {
  const own = features.filter((f) => f.application_id === app.id);
  return {
    id: app.id,
    name: app.name,
    slug: app.slug,
    department_id: app.department_id,
    tagline: app.tagline,
    description: app.description,
    audience: app.audience,
    status: app.status,
    url: app.url ?? '',
    stack: app.stack,
    version: app.version,
    sort_order: app.sort_order,
    logo_url: app.logo_url ?? '',
    category: app.category ?? 'custom',
    screenshots: appScreenshots(app),
    developer: app.developer ?? '',
    time_saved: app.time_saved ?? '',
    benefits: appBenefits(app).length ? appBenefits(app) : [''],
    show_on_status: app.show_on_status !== 0,
    cost_saved: app.cost_saved ?? '',
    impact_note: app.impact_note ?? '',
    features: own.length ? own.map((f) => ({ title: f.title, detail: f.detail })) : [{ title: '', detail: '' }],
  };
}

/** Admin → Applications: add, edit and remove catalog apps. */
export function AdminAppsPanel() {
  const [apps, setApps] = useState<Application[] | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [newDeptName, setNewDeptName] = useState('');
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await client.catalog();
    setApps(data.applications);
    setDepartments(data.departments);
    setFeatures(data.features);
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, []);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  function openNew() {
    setFormError('');
    setNewDeptName('');
    setDraft(emptyDraft(departments[0]?.id ?? ''));
  }

  function openEdit(app: Application) {
    setFormError('');
    setNewDeptName('');
    setDraft(draftFrom(app, features));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setFormError('');
    setBusy(true);
    try {
      let departmentId = draft.department_id;
      if (departmentId === NEW_DEPT) {
        const { department } = await client.createDepartment(newDeptName);
        departmentId = department.id;
      }
      const { id, ...rest } = draft;
      const body: AppInput = {
        ...rest,
        department_id: departmentId,
        url: draft.url?.trim() || null,
        logo_url: draft.logo_url?.trim() || null,
        screenshots: draft.screenshots.map((s) => s.trim()).filter(Boolean),
        benefits: draft.benefits.map((b) => b.trim()).filter(Boolean),
        features: draft.features.filter((f) => f.title.trim()),
      };
      if (id) await client.updateApp(id, body);
      else await client.createApp(body);
      await load();
      setDraft(null);
      setInfo(id ? `Saved ${draft.name}.` : `Added ${draft.name}. It now appears in Applications and on the Status page.`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
      // A new department may have been created before the app failed; refresh the list.
      load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  async function remove(app: Application) {
    if (!window.confirm(`Delete ${app.name}? Its features and Status page entry will be removed too. This can't be undone.`)) return;
    setError('');
    setInfo('');
    try {
      await client.deleteApp(app.id);
      await load();
      setInfo(`Deleted ${app.name}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  if (!apps) return error ? <Alert severity="error">{error}</Alert> : <Skeleton variant="rounded" height={320} />;

  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? '—';

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="success">{info}</Alert>}

      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2} mb={2.5}>
            <Box>
              <Typography variant="h6">Applications</Typography>
              <Typography color="text.secondary">
                Apps listed here appear on Home, Applications and the Status page.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ flex: 'none' }}>
              Add application
            </Button>
          </Stack>

          <Box sx={{ border: `1px solid ${tokens.divider}`, borderRadius: 2, overflow: 'hidden' }}>
            {apps.length === 0 && (
              <Typography sx={{ p: 2.5 }} color="text.secondary">
                No applications yet.
              </Typography>
            )}
            {apps.map((app, i) => (
              <Stack
                key={app.id}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                gap={2}
                sx={{ px: 2.5, py: 2, borderTop: i ? `1px solid ${tokens.divider}` : 0 }}
              >
                <Stack direction="row" alignItems="center" gap={2} flex={1} minWidth={0}>
                  <AppMonogram name={app.name} slug={app.slug} logoUrl={app.logo_url} size="sm" />
                  <Box minWidth={0}>
                    <Link component={RouterLink} to={`/apps/${app.slug}`} color="inherit" fontWeight={600}>
                      {app.name}
                    </Link>
                    <Typography fontSize={14} color="text.secondary" noWrap>
                      {categoryLabel(app.category)} · {deptName(app.department_id)}
                      {app.show_on_status === 0 ? ' · hidden from Status' : ''}
                      {app.version ? ` · v${app.version}` : ''}
                      {app.url ? '' : ' · no link yet'}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <AppStatusChip status={app.status} />
                  <Button size="small" variant="outlined" onClick={() => openEdit(app)}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => remove(app)} aria-label={`Delete ${app.name}`}>
                    Delete
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={Boolean(draft)} onClose={() => !busy && setDraft(null)} fullWidth maxWidth="md" scroll="paper">
        {draft && (
          <Box component="form" onSubmit={save} sx={{ display: 'contents' }}>
            <DialogTitle sx={{ fontWeight: 700 }}>{draft.id ? `Edit ${draft.name || 'application'}` : 'Add application'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                {formError && <Alert severity="error">{formError}</Alert>}

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField label="Name" value={draft.name} onChange={(e) => set('name', e.target.value)} required fullWidth autoFocus />
                  <TextField
                    label="Slug (web address)"
                    value={draft.slug}
                    onChange={(e) => set('slug', e.target.value)}
                    placeholder="made from the name if empty"
                    helperText={`/apps/${draft.slug || 'name-of-app'}`}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField
                    select
                    label="Department"
                    value={draft.department_id}
                    onChange={(e) => set('department_id', e.target.value)}
                    required
                    fullWidth
                  >
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                    <MenuItem value={NEW_DEPT}>+ New department…</MenuItem>
                  </TextField>
                  <TextField select label="Status" value={draft.status} onChange={(e) => set('status', e.target.value as Application['status'])} fullWidth>
                    {STATUSES.map((s) => (
                      <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  select
                  label="Category"
                  value={draft.category}
                  onChange={(e) => {
                    const category = e.target.value as AppCategory;
                    // New NetSuite apps start hidden from the Status page; admins can switch it back on.
                    setDraft((d) => (d ? { ...d, category, ...(d.id ? {} : { show_on_status: category !== 'netsuite' }) } : d));
                  }}
                  fullWidth
                >
                  {APP_CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Box>
                  <FormControlLabel
                    control={<Switch checked={draft.show_on_status} onChange={(e) => set('show_on_status', e.target.checked)} />}
                    label="Show on Status page"
                  />
                  <Typography fontSize={14} color="text.secondary" sx={{ ml: 6.5, mt: -0.5 }}>
                    {draft.show_on_status
                      ? 'Listed on the Status page with its health and incidents.'
                      : 'Hidden from the Status page and the Home page health panel.'}
                  </Typography>
                </Box>
                {draft.department_id === NEW_DEPT && (
                  <TextField label="New department name" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} required fullWidth />
                )}

                <TextField label="Tagline" value={draft.tagline} onChange={(e) => set('tagline', e.target.value)} placeholder="One line on what it does" fullWidth />
                <TextField label="Description" value={draft.description} onChange={(e) => set('description', e.target.value)} multiline minRows={3} fullWidth />

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField label="App link" value={draft.url ?? ''} onChange={(e) => set('url', e.target.value)} placeholder="https://…" helperText="Shows an Open app button" fullWidth />
                  <TextField
                    label="Logo"
                    value={draft.logo_url ?? ''}
                    onChange={(e) => set('logo_url', e.target.value)}
                    placeholder="https://… or /app-logos/name.svg"
                    helperText="Optional — initials are used if empty"
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField label="Audience" value={draft.audience} onChange={(e) => set('audience', e.target.value)} placeholder="Who uses it" fullWidth />
                  <TextField label="Version" value={draft.version} onChange={(e) => set('version', e.target.value)} sx={{ minWidth: { sm: 140 } }} />
                  <TextField
                    label="Order"
                    type="number"
                    value={draft.sort_order}
                    onChange={(e) => set('sort_order', Number(e.target.value))}
                    helperText="Lower shows first"
                    sx={{ minWidth: { sm: 120 } }}
                  />
                </Stack>
                <TextField label="Stack" value={draft.stack} onChange={(e) => set('stack', e.target.value)} placeholder="React · Cloudflare Pages · D1" fullWidth />

                <Box>
                  <Typography fontWeight={700}>Impact</Typography>
                  <Typography fontSize={14} color="text.secondary" mb={1.5}>
                    Shown on the app page. Leave empty to hide.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                      <TextField label="Developer" value={draft.developer} onChange={(e) => set('developer', e.target.value)} placeholder="Who built it" fullWidth />
                      <TextField
                        label="Time saved"
                        value={draft.time_saved}
                        onChange={(e) => set('time_saved', e.target.value)}
                        placeholder="≈ 6 hours a week"
                        helperText="Short headline for the AST team"
                        fullWidth
                      />
                      <TextField
                        label="Est. cost saving"
                        value={draft.cost_saved}
                        onChange={(e) => set('cost_saved', e.target.value)}
                        placeholder="≈ S$13,000 a year"
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      label="How it was estimated"
                      value={draft.impact_note}
                      onChange={(e) => set('impact_note', e.target.value)}
                      placeholder="e.g. 8 people × 30 min a week, 48 weeks, at S$45 an hour"
                      helperText="Shown under the figures so readers know they are estimates"
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    {draft.benefits.map((b, i) => (
                      <Stack key={i} direction="row" gap={1.5} alignItems="center">
                        <TextField
                          label={`Benefit ${i + 1}`}
                          value={b}
                          onChange={(e) => set('benefits', draft.benefits.map((x, j) => (j === i ? e.target.value : x)))}
                          placeholder="e.g. No more merging weekly Excel files by hand"
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <IconButton aria-label={`Remove benefit ${i + 1}`} onClick={() => set('benefits', draft.benefits.filter((_, j) => j !== i))}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    ))}
                    {draft.benefits.length < 10 && (
                      <Box>
                        <Button startIcon={<AddIcon />} onClick={() => set('benefits', [...draft.benefits, ''])} sx={{ textDecoration: 'none' }}>
                          Add benefit
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography fontWeight={700}>Screenshots</Typography>
                  <Typography fontSize={14} color="text.secondary" mb={1.5}>
                    The first one is the card image. Use an https:// URL or a site path like /apps-imgs/name.webp.
                  </Typography>
                  <Stack spacing={1.5}>
                    {draft.screenshots.map((src, i) => (
                      <Stack key={i} direction="row" gap={1.5} alignItems="center">
                        <Box
                          sx={{ width: 72, height: 45, flex: 'none', borderRadius: 1, overflow: 'hidden', bgcolor: tokens.strip, border: `1px solid ${tokens.divider}` }}
                        >
                          {src.trim() && <Box component="img" src={src.trim()} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} />}
                        </Box>
                        <TextField
                          label={i === 0 ? 'Screenshot 1 (card image)' : `Screenshot ${i + 1}`}
                          value={src}
                          onChange={(e) => set('screenshots', draft.screenshots.map((x, j) => (j === i ? e.target.value : x)))}
                          placeholder="/apps-imgs/name.webp"
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <IconButton aria-label={`Remove screenshot ${i + 1}`} onClick={() => set('screenshots', draft.screenshots.filter((_, j) => j !== i))}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    ))}
                    {draft.screenshots.length < 8 && (
                      <Box>
                        <Button startIcon={<AddIcon />} onClick={() => set('screenshots', [...draft.screenshots, ''])} sx={{ textDecoration: 'none' }}>
                          Add screenshot
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Box>
                  <Typography fontWeight={700} mb={1.5}>
                    Features
                  </Typography>
                  <Stack spacing={1.5}>
                    {draft.features.map((f, i) => (
                      <Stack key={i} direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'flex-start' }}>
                        <TextField
                          label={`Feature ${i + 1}`}
                          value={f.title}
                          onChange={(e) => set('features', draft.features.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Detail"
                          value={f.detail}
                          onChange={(e) => set('features', draft.features.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)))}
                          sx={{ flex: 2 }}
                        />
                        <IconButton
                          aria-label={`Remove feature ${i + 1}`}
                          onClick={() => set('features', draft.features.filter((_, j) => j !== i))}
                          sx={{ mt: { sm: 1 } }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    ))}
                    <Box>
                      <Button startIcon={<AddIcon />} onClick={() => set('features', [...draft.features, { title: '', detail: '' }])} sx={{ textDecoration: 'none' }}>
                        Add feature
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setDraft(null)} disabled={busy} sx={{ textDecoration: 'none' }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={busy}>
                {busy ? 'Saving…' : draft.id ? 'Save changes' : 'Add application'}
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}
